import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Header from '../components/Header/Header';
import SEO from '../components/SEO/SEO';
import { API_BASE_URL } from '../utils/apiConfig';
import './AltcoinIndexPage.css';

function AltcoinIndexPage() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedFaq, setExpandedFaq] = useState(null);

  const faqData = [
    {
      question: t('altcoinIndex.faq.q1', 'Why 75 is the threshold for Altcoin Season?'),
      answer: t('altcoinIndex.faq.a1', 'When 75% or more of the top 100 altcoins outperform Bitcoin over 30 days, it indicates broad-based altcoin strength — not just a few outliers. This level historically signals sustained capital rotation from BTC into altcoins, creating favorable conditions for altcoin trading.')
    },
    {
      question: t('altcoinIndex.faq.q2', 'Why use 30-day performance instead of shorter periods?'),
      answer: t('altcoinIndex.faq.a2', '30 days filters out noise and daily volatility. Shorter timeframes (7d, 14d) produce false signals during consolidations. A 30-day window captures meaningful trends while remaining responsive enough for tactical decisions. It balances reliability with timeliness.')
    },
    {
      question: t('altcoinIndex.faq.q3', 'How should I trade based on the index?'),
      answer: t('altcoinIndex.faq.a3', 'During Bitcoin Season (index <25), focus on BTC accumulation — altcoins typically underperform. During Altcoin Season (index >75), increase altcoin exposure, especially to small caps. In neutral zones (25-75), use selective positioning based on sector strength rather than broad altcoin bets.')
    },
    {
      question: t('altcoinIndex.faq.q4', 'Which coins are excluded from the calculation?'),
      answer: t('altcoinIndex.faq.a4', 'Stablecoins (USDT, USDC, DAI) and wrapped tokens (WBTC, WETH) are excluded since they track other assets by design. We only count genuine altcoins that can independently outperform or underperform Bitcoin based on their own market dynamics.')
    }
  ];

  // Add FAQ Schema to page
  useEffect(() => {
    const faqSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": faqData.map(faq => ({
        "@type": "Question",
        "name": faq.question,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": faq.answer
        }
      }))
    };

    let faqScript = document.querySelector('script[data-schema="altcoin-faq"]');
    if (!faqScript) {
      faqScript = document.createElement('script');
      faqScript.setAttribute('type', 'application/ld+json');
      faqScript.setAttribute('data-schema', 'altcoin-faq');
      document.head.appendChild(faqScript);
    }
    faqScript.textContent = JSON.stringify(faqSchema);

    return () => {
      const script = document.querySelector('script[data-schema="altcoin-faq"]');
      if (script) script.remove();
    };
  }, [faqData]);

  const fetchIndex = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/altcoin-index`);
      const result = await response.json();

      if (result.success) {
        setData(result);
      } else {
        setError(result.error || 'Failed to fetch data');
      }
    } catch (err) {
      setError('Failed to fetch Altcoin Season Index');
      console.error('Altcoin Index fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchIndex();
    // Refresh every 10 minutes
    const interval = setInterval(fetchIndex, 600000);
    return () => clearInterval(interval);
  }, [fetchIndex]);

  const getSeasonLabel = (season) => {
    switch (season) {
      case 'bitcoin': return t('altcoinIndex.bitcoinSeason', 'Bitcoin Season');
      case 'altcoin': return t('altcoinIndex.altcoinSeason', 'Altcoin Season');
      default: return t('altcoinIndex.neutral', 'Neutral');
    }
  };

  const formatPercent = (value) => {
    if (value === undefined || value === null) return '0.00%';
    const sign = value >= 0 ? '+' : '';
    return `${sign}${value.toFixed(2)}%`;
  };

  const getSliderPosition = (index) => {
    return `${index}%`;
  };

  return (
    <div className="altcoin-index-page">
      <SEO page="altcoinIndex" />
      <Header />

      <div className="altcoin-index-content">
        <div className="altcoin-index-header">
          <h1 className="altcoin-index-title">{t('altcoinIndex.title', 'Altcoin Season Index')}</h1>
          <p className="altcoin-index-subtitle">
            {t('altcoinIndex.subtitle', 'The Altcoin Season Index tracks the performance of the top 100 altcoins compared to Bitcoin over the last 30 days.')}
          </p>
        </div>

        {loading ? (
          <div className="altcoin-index-loading">
            <div className="spinner"></div>
            <p>{t('altcoinIndex.loading', 'Calculating index...')}</p>
          </div>
        ) : error ? (
          <div className="altcoin-index-error">
            <p>{error}</p>
            <button onClick={fetchIndex}>{t('altcoinIndex.retry', 'Retry')}</button>
          </div>
        ) : data && (
          <>
            {/* Stats Grid */}
            <div className="stats-grid">
              {/* Main Index Card */}
              <div className="index-card">
                <div className="index-label">{t('altcoinIndex.currentIndex', 'Current Index')}</div>
                <div className="index-value">
                  <span className={`index-number ${data.season}`}>{data.index}</span>
                  <span className="index-total">/100</span>
                </div>
                <span className={`season-badge ${data.season}`}>
                  {getSeasonLabel(data.season)}
                </span>
              </div>

              {/* Stats Cards */}
              <div className="stat-card">
                <div className="stat-header">
                  <span className="stat-label">{t('altcoinIndex.btcPerformance', 'BTC 30D')}</span>
                </div>
                <div className={`stat-value ${data.btcChange >= 0 ? 'positive' : 'negative'}`}>
                  {formatPercent(data.btcChange)}
                </div>
              </div>

              <div className="stat-card">
                <div className="stat-header">
                  <span className="stat-label">{t('altcoinIndex.outperforming', 'Outperforming BTC')}</span>
                </div>
                <div className="stat-value">
                  {data.outperformingCount}/{data.totalCoins}
                </div>
              </div>
            </div>

            {/* Slider Visualization */}
            <div className="slider-section">
              <div className="slider-header">
                <span className="slider-label bitcoin">{t('altcoinIndex.bitcoinSeason', 'Bitcoin Season')}</span>
                <span className="slider-label altcoin">{t('altcoinIndex.altcoinSeason', 'Altcoin Season')}</span>
              </div>
              <div className="slider-track">
                <div className="slider-thumb" style={{ left: getSliderPosition(data.index) }}></div>
              </div>
              <div className="slider-scale">
                <span>0</span>
                <span>25</span>
                <span>50</span>
                <span>75</span>
                <span>100</span>
              </div>
            </div>

            {/* Top Performers Grid */}
            <div className="performers-section">
              {/* Top Gainers */}
              <div className="performers-card">
                <h3 className="performers-title">{t('altcoinIndex.topGainers', 'Top Gainers (30D)')}</h3>
                {data.topGainers?.map((coin, index) => (
                  <div key={coin.id} className="performer-item">
                    <div className="performer-info">
                      <img className="crypto-icon" src={coin.image} alt={coin.symbol} />
                      <span className="performer-symbol">{coin.symbol}</span>
                    </div>
                    <div className={`performer-bar ${coin.change >= 0 ? 'positive' : 'negative'}`}>
                      <span>{formatPercent(coin.change)}</span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Top Losers */}
              <div className="performers-card">
                <h3 className="performers-title">{t('altcoinIndex.topLosers', 'Top Losers (30D)')}</h3>
                {data.topLosers?.map((coin, index) => (
                  <div key={coin.id} className="performer-item">
                    <div className="performer-info">
                      <img className="crypto-icon" src={coin.image} alt={coin.symbol} />
                      <span className="performer-symbol">{coin.symbol}</span>
                    </div>
                    <div className={`performer-bar ${coin.change >= 0 ? 'positive' : 'negative'}`}>
                      <span>{formatPercent(coin.change)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Explanation */}
            <div className="explanation-section">
              <h3>{t('altcoinIndex.howItWorks', 'How It Works')}</h3>
              <p>
                {t('altcoinIndex.explanation', 'The index measures what percentage of the top 100 altcoins (excluding stablecoins and wrapped tokens) have outperformed Bitcoin over the last 30 days. A score of 75-100 indicates "Altcoin Season" where most altcoins are outperforming Bitcoin. A score of 0-25 indicates "Bitcoin Season" where Bitcoin is outperforming most altcoins.')}
              </p>
            </div>

            {/* FAQ Section */}
            <div className="altcoin-faq-section">
              <h3>{t('altcoinIndex.faqTitle', 'Frequently Asked Questions')}</h3>
              <div className="altcoin-faq-list">
                {faqData.map((faq, index) => (
                  <div
                    key={index}
                    className={`altcoin-faq-item ${expandedFaq === index ? 'expanded' : ''}`}
                    onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                  >
                    <div className="altcoin-faq-question">
                      <h4>{faq.question}</h4>
                      <span className="altcoin-faq-toggle">{expandedFaq === index ? '−' : '+'}</span>
                    </div>
                    {expandedFaq === index && (
                      <div className="altcoin-faq-answer">
                        <p>{faq.answer}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default AltcoinIndexPage;
